import React, { useEffect, useState } from 'react';
import { CalendarClock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import api from '../lib/api';

const GCAL = {
  synced: ['Synchronisé avec Google Agenda', 'text-emerald-700 bg-emerald-50'],
  deleted: ['Événement Google supprimé', 'text-slate-600 bg-slate-100'],
  error: ['Non synchronisé avec Google Agenda', 'text-red-700 bg-red-50'],
};

const GcalLine = ({ q, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const g = q.gcal;
  if (!g?.state) return null;
  const [txt, cls] = GCAL[g.state] || GCAL.error;
  const retry = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/google-calendar/sync/${q.id}`);
      onUpdated({ gcal: r.data.gcal });
      (r.data.gcal?.state === 'error' ? toast.error : toast.success)(r.data.gcal?.state === 'error' ? 'Échec de la synchronisation' : 'Synchronisé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    setBusy(false);
  };
  return (
    <div className={`mt-3 rounded-xl px-3 py-2 text-sm flex flex-wrap items-center gap-2 ${cls}`} data-testid="gcal-sync-state">
      <span className="font-semibold">{txt}</span>
      {g.state === 'error' && <span className="text-xs" data-testid="gcal-sync-error">{g.last_error} ({g.attempts} tentative(s))</span>}
      {g.state === 'error' && (
        <Button size="sm" variant="outline" className="ml-auto" onClick={retry} disabled={busy} data-testid="gcal-retry-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />Réessayer
        </Button>
      )}
    </div>
  );
};

const Reschedule = ({ q, onUpdated }) => {
  const [day, setDay] = useState('');
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!day) return;
    setTime('');
    api.get('/admin/availability/preview', { params: { service: q.services[0], options: (q.options || []).join(','), start: day, days: 1 } })
      .then((r) => setSlots(r.data.days.find((d) => d.date === day)?.slots || []))
      .catch(() => setSlots([]));
  }, [day, q.services, q.options]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/reservations/${q.id}/reschedule`, { date: day, time });
      onUpdated(r.data);
      toast.success('Rendez-vous déplacé');
      setDay(''); setSlots([]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Déplacement impossible'); }
    setBusy(false);
  };

  return (
    <div className="mt-3 p-3 rounded-xl border border-slate-200" data-testid="reschedule-box">
      <p className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />Déplacer le rendez-vous</p>
      <div className="flex flex-wrap gap-2 items-center">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="reschedule-date" />
        <select value={time} onChange={(e) => setTime(e.target.value)} disabled={!day} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="reschedule-time">
          <option value="">{day && !slots.length ? 'Aucun créneau libre' : 'Heure'}</option>
          {slots.map((s) => <option key={s.time} value={s.time}>{s.time}</option>)}
        </select>
        <Button size="sm" onClick={submit} disabled={!day || !time || busy} data-testid="reschedule-submit">Déplacer</Button>
      </div>
    </div>
  );
};

export const ReservationTools = ({ q, onUpdated }) => {
  if (q.kind !== 'reservation') return null;
  return (
    <>
      <GcalLine q={q} onUpdated={onUpdated} />
      {['pending', 'confirmed'].includes(q.status) && <Reschedule q={q} onUpdated={onUpdated} />}
    </>
  );
};
